import React, { useState, useEffect, useContext } from "react";
import {
  Calendar,
  Clock,
  Info,
  User,
  Stethoscope,
  FileText,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Heart,
  CreditCard,
  Star,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { AuthContext } from "../../../../context/AuthContext";
import PaymentComponent from "../../../Payment/PaymentComponent";
import { appointmentTypes, dayNames, monthNames, steps, timeSlots } from "../../../../data";

const AppointmentForm = ({ className = "" }) => {
  const { getDoctors, doctors, getPets, pets, getPetAppointment } =
    useContext(AuthContext);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    pets: "",
    type: "",
    date: "",
    time: "",
    doctor: "",
    status: "pending",
    notes: "",
    paymentMethod: "razorpay",
  });

  // Payment state
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  let totalFee = 0;

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const totalSteps = 7;

  
  useEffect(() => {
    if (doctors?.length === 0) {
      getDoctors();
    }
    if (pets === null) {
      getPets();
    }
  }, [getDoctors, doctors, getPets, pets]);

  // Handle payment success callback
  const handlePaymentSuccess = async (paymentDetails) => {
    console.log("Payment Success Details:", paymentDetails);
    setIsPaymentComplete(true);
    setPaymentData(paymentDetails);

    // Update form data with payment information
    const updatedFormData = {
      ...formData,
      paymentId: paymentDetails.paymentId,
      orderId: paymentDetails.orderId,
      paymentStatus: "completed",
      paymentMethod: "razorpay",
      paidAmount: paymentDetails.amount,
    };

    setFormData(updatedFormData);
  };

  // Handle payment failure callback
  const handlePaymentFailure = (error) => {
    setIsPaymentComplete(false);
    setPaymentData(null);
    setErrors({
      payment: error.message || "Payment failed. Please try again.",
    });
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle card selection
  const handleCardSelection = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate current step
  const validateCurrentStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1:
        if (!formData.pets) {
          newErrors.pets = "Please select a pet";
        }
        break;
      case 2:
        if (!formData.type) {
          newErrors.type = "Please select appointment type";
        }
        break;
      case 3:
        if (!formData.date) {
          newErrors.date = "Please select a date";
        } else {
          const selectedDate = new Date(formData.date + "T00:00:00");
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (selectedDate < today) {
            newErrors.date = "Date cannot be in the past";
          }
        }
        if (!formData.time) {
          newErrors.time = "Please select a time";
        }
        break;
      case 4:
        if (!formData.doctor) {
          newErrors.doctor = "Please select a doctor";
        }
        break;
      case 5:
        // No validation needed for notes (optional)
        break;
      case 6:
        if (!isPaymentComplete) {
          newErrors.payment = "Please complete the payment to proceed";
        }
        getPetAppointment();
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateCurrentStep()) {
      setCompletedSteps((prev) => new Set([...prev, currentStep]));
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle step click
  const handleStepClick = (stepNumber) => {
    if (stepNumber <= currentStep || completedSteps.has(stepNumber - 1)) {
      setCurrentStep(stepNumber);
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      pets: "",
      type: "",
      date: "",
      time: "",
      doctor: "",
      status: "pending",
      notes: "",
      paymentMethod: "razorpay",
    });
    setErrors({});
    setSuccessMessage("");
    setCompletedSteps(new Set());
    setIsPaymentComplete(false);
    setPaymentData(null);
  };

  // Print function
  // Replace the existing handlePrint function with this enhanced version
  const handlePrint = () => {
    // Calculate total fee
    const totalFee = (selectedDoctor?.consultationFee || 1500) + 1;

    // Get appointment ID
    const appointmentId =
      paymentData?.orderId || "APT-" + Date.now().toString().slice(-6);

    // Create the print content
    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Appointment Confirmation - ${appointmentId}</title>
      <style>
        @media print {
          @page {
            size: A4;
            margin: 20mm;
          }
          
          body {
            margin: 0;
            padding: 0;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            line-height: 1.4;
          }
          
          .no-print {
            display: none !important;
          }
        }
        
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #333;
          line-height: 1.4;
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .print-card {
          border: 2px solid #10b981;
          border-radius: 12px;
          background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
          padding: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .header {
          text-align: center;
          border-bottom: 2px solid #10b981;
          padding-bottom: 20px;
          margin-bottom: 25px;
        }
        
        .header h1 {
          color: #059669;
          font-size: 28px;
          font-weight: bold;
          margin: 0 0 5px 0;
        }
        
        .header .subtitle {
          color: #065f46;
          font-size: 16px;
          margin-bottom: 10px;
        }
        
        .appointment-id {
          background: #3b82f6;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-family: monospace;
          font-weight: bold;
          display: inline-block;
          margin-top: 10px;
        }
        
        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 25px;
          margin-bottom: 25px;
        }
        
        .detail-section {
          background: white;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #d1fae5;
        }
        
        .detail-section h3 {
          color: #059669;
          font-size: 16px;
          font-weight: bold;
          margin: 0 0 15px 0;
          border-bottom: 1px solid #d1fae5;
          padding-bottom: 8px;
        }
        
        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }
        
        .detail-label {
          font-weight: 600;
          color: #374151;
        }
        
        .detail-value {
          color: #1f2937;
          text-align: right;
          flex: 1;
          margin-left: 10px;
        }
        
        .schedule-banner {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
          margin-bottom: 25px;
        }
        
        .schedule-banner h3 {
          margin: 0 0 15px 0;
          font-size: 18px;
        }
        
        .schedule-details {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 15px;
          margin-top: 15px;
        }
        
        .schedule-item {
          text-align: center;
        }
        
        .schedule-item .label {
          font-size: 12px;
          opacity: 0.9;
          margin-bottom: 5px;
        }
        
        .schedule-item .value {
          font-size: 16px;
          font-weight: bold;
        }
        
        .payment-section {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 25px;
        }
        
        .payment-section h3 {
          color: #1e293b;
          margin: 0 0 15px 0;
          font-size: 16px;
        }
        
        .payment-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        
        .bill-breakdown {
          background: white;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        .bill-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }
        
        .bill-total {
          border-top: 2px solid #10b981;
          padding-top: 10px;
          margin-top: 10px;
          font-weight: bold;
          font-size: 16px;
          color: #059669;
        }
        
        .status-confirmed {
          background: #10b981;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: bold;
          display: inline-block;
        }
        
        .notes-section {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 25px;
        }
        
        .notes-section h4 {
          color: #92400e;
          margin: 0 0 10px 0;
          font-size: 14px;
          font-weight: bold;
        }
        

        
        .footer {
          text-align: center;
          border-top: 1px solid #d1fae5;
          padding-top: 20px;
          color: #6b7280;
          font-size: 14px;
        }
        
        .footer .thank-you {
          font-size: 16px;
          font-weight: bold;
          color: #059669;
          margin-bottom: 5px;
        }
        
        .print-date {
          position: absolute;
          top: 10px;
          right: 10px;
          font-size: 12px;
          color: #6b7280;
        }
        
        @media screen {
          .print-card {
            max-width: 800px;
            margin: 20px auto;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-date">Printed: ${new Date().toLocaleString()}</div>
      
      <div class="print-card">
        <!-- Header -->
        <div class="header">
          <h1>🐾 Animalia+ Veterinary</h1>
          <div class="subtitle">Appointment Confirmation</div>
          <div class="appointment-id">ID: ${appointmentId}</div>
        </div>
        
        <!-- Schedule Banner -->
        <div class="schedule-banner">
          <h3>📅 Appointment Schedule</h3>
          <div class="schedule-details">
            <div class="schedule-item">
              <div class="label">Date</div>
              <div class="value">${formatDate(formData.date)}</div>
            </div>
            <div class="schedule-item">
              <div class="label">Time</div>
              <div class="value">${formData.time}</div>
            </div>
            <div class="schedule-item">
              <div class="label">Status</div>
              <div class="value">✅ Appoinment Booked</div>
            </div>
          </div>
        </div>
        
        <!-- Details Grid -->
        <div class="details-grid">
          <!-- Pet Information -->
          <div class="detail-section">
            <h3>🐕 Pet Information</h3>
            <div class="detail-row">
              <span class="detail-label">Pet Name:</span>
              <span class="detail-value">${selectedPet?.name || "N/A"}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Species:</span>
              <span class="detail-value">${selectedPet?.species || "N/A"}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Breed:</span>
              <span class="detail-value">${selectedPet?.breed || "N/A"}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Owner:</span>
              <span class="detail-value">${
                selectedPet?.ownerId?.userDetails?.name || ""
              } ${selectedPet?.ownerId?.userDetails?.lastName || ""}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Appointment Type:</span>
              <span class="detail-value">${
                appointmentTypes?.find((t) => t.value === formData.type)
                  ?.label || "N/A"
              }</span>
            </div>
          </div>
          
          <!-- Doctor Information -->
          <div class="detail-section">
            <h3>👨‍⚕️ Doctor Information</h3>
            <div class="detail-row">
              <span class="detail-label">Doctor:</span>
              <span class="detail-value">Dr. ${
                selectedDoctor?.name || "N/A"
              }</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Specialization:</span>
              <span class="detail-value">${
                selectedDoctor?.specialization?.[0] || "General Medicine"
              }</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Experience:</span>
              <span class="detail-value">${
                selectedDoctor?.experience || 5
              } years</span>
            </div>
            
            <div class="detail-row">
              <span class="detail-label">Consultation Fee:</span>
              <span class="detail-value">₹${
                selectedDoctor?.consultationFee || 1500
              }</span>
            </div>
          </div>
        </div>
        
        <!-- Payment Section -->
        <div class="payment-section">
          <h3>💳 Payment Details</h3>
          <div class="payment-grid">
            <div>
              <div class="detail-row">
                <span class="detail-label">Payment ID:</span>
                <span class="detail-value">${
                  paymentData?.paymentId || "N/A"
                }</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Order ID:</span>
                <span class="detail-value">${
                  paymentData?.orderId || "N/A"
                }</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Payment Method:</span>
                <span class="detail-value">Razorpay</span>
              </div>
            </div>
            <div class="bill-breakdown">
              <div class="bill-row">
                <span>Consultation Fee:</span>
                <span>₹${selectedDoctor?.consultationFee || 1500}</span>
              </div>
              <div class="bill-row">
                <span>Service Charge:</span>
                <span>₹1</span>
              </div>
              <div class="bill-row bill-total">
                <span>Total Paid:</span>
                <span>₹${totalFee}</span>
              </div>
            </div>
          </div>
        </div>
        
        ${
          formData.notes
            ? `
        <!-- Notes Section -->
        <div class="notes-section">
          <h4>📝 Additional Notes:</h4>
          <p style="margin: 0; color: #92400e;">${formData.notes}</p>
        </div>
        `
            : ""
        }
        
        
      </div>
    </body>
    </html>
  `;

    // Create a new window for printing
    const printWindow = window.open("", "_blank");

    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();

        // Close the print window after printing (optional)
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
    } else {
      // Fallback if popup is blocked
      alert("Please allow popups to print the appointment confirmation.");
    }
  };

  // Format date for display - FIXED VERSION
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Create date object using the date string directly
    const date = new Date(dateString + "T12:00:00"); // Add time to avoid timezone issues
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // FIXED: Proper date formatting for input
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [calendarDate, setCalendarDate] = useState(new Date());

  const generateCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const daysInMonth = getDaysInMonth(calendarDate);
    const firstDay = getFirstDayOfMonth(calendarDate);

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse selected date properly
    const selectedDate = formData.date
      ? new Date(formData.date + "T00:00:00")
      : null;

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isDisabled = isDateDisabled(date);
      const isSelected =
        selectedDate &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      const isToday = date.toDateString() === today.toDateString();

      days.push({
        day,
        date,
        isDisabled,
        isSelected,
        isToday,
      });
    }

    return days;
  };

  // FIXED: Date selection handler
  const handleDateSelect = (date) => {
    if (!isDateDisabled(date)) {
      const formattedDate = formatDateForInput(date);
      setFormData((prev) => ({
        ...prev,
        date: formattedDate,
      }));

      // Clear error
      if (errors.date) {
        setErrors((prev) => ({
          ...prev,
          date: "",
        }));
      }
    }
  };

  const navigateMonth = (direction) => {
    setCalendarDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  

  // Get selected pet and doctor details
  const selectedPet = pets?.find((pet) => pet._id === formData.pets);
  const selectedDoctor = doctors?.find(
    (doctor) => doctor._id === formData.doctor
  );

  // Enhanced Mobile-Responsive Step Content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="">
            {/* Pet Selection Grid with Scroll */}
            <div className="grid sm:grid-cols-2 gap-3 ">
                {pets?.map((pet) => (
                  <div
                    key={pet._id}
                    onClick={() => handleCardSelection("pets", pet._id)}
                    className={`group p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5 relative overflow-hidden ${
                      formData.pets === pet._id
                        ? "border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg "
                        : "border-gray-200 hover:border-blue-300 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50"
                    }`}
                  >
                    {/* Selection Indicator */}
                    {formData.pets === pet._id && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      {/* Pet Avatar */}
                      <div className="relative">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center text-white text-lg sm:text-xl font-bold shadow-md group-hover:shadow-lg transition-shadow">
                          {pet.name.charAt(0)}
                        </div>
                      </div>

                      {/* Pet Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-800 text-base sm:text-lg truncate">
                          {pet.name}
                        </h4>
                        <p className="text-sm text-gray-600 font-medium">
                          {pet.species} • {pet.breed}
                        </p>

                        {/* Pet Stats */}
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {(() => {
                              if (!pet.dateOfBirth) return "Unknown age";

                              const today = new Date();
                              const birthDate = new Date(pet.dateOfBirth);

                              // Time differences
                              const diffMs = today - birthDate;
                              const diffDays = Math.floor(
                                diffMs / (1000 * 60 * 60 * 24)
                              );
                              const diffMonths =
                                today.getMonth() -
                                birthDate.getMonth() +
                                12 *
                                  (today.getFullYear() -
                                    birthDate.getFullYear());
                              const diffYears =
                                today.getFullYear() - birthDate.getFullYear();

                              // Adjust years properly
                              let ageYears = diffYears;
                              if (
                                today.getMonth() < birthDate.getMonth() ||
                                (today.getMonth() === birthDate.getMonth() &&
                                  today.getDate() < birthDate.getDate())
                              ) {
                                ageYears--;
                              }

                              if (ageYears >= 1) {
                                return `${ageYears} year${
                                  ageYears > 1 ? "s" : ""
                                } old`;
                              } else if (diffMonths >= 1) {
                                return `${diffMonths} month${
                                  diffMonths > 1 ? "s" : ""
                                } old`;
                              } else {
                                return `${diffDays} day${
                                  diffDays !== 1 ? "s" : ""
                                } old`;
                              }
                            })()}
                          </span>

                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            {pet.weight}kg
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            {/* Scroll Indicator */}
            {pets?.length > 3 && (
              <div className="text-center">
                <p className="text-xs text-gray-500 flex items-center justify-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                  Scroll to see more pets ({pets.length} total)
                </p>
              </div>
            )}

            {/* Error Message */}
            {errors.pets && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">
                  {errors.pets}
                </p>
              </div>
            )}

            <style jsx>{`
              /* Custom scrollbar styles */
              .scrollbar-thin {
                scrollbar-width: thin;
              }

              .scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
                background-color: #d1d5db;
                border-radius: 0.375rem;
              }

              .scrollbar-track-gray-100::-webkit-scrollbar-track {
                background-color: #f3f4f6;
                border-radius: 0.375rem;
              }

              .scrollbar-thin::-webkit-scrollbar {
                width: 6px;
              }

              .scrollbar-thin::-webkit-scrollbar-thumb {
                background-color: #d1d5db;
                border-radius: 0.375rem;
              }

              .scrollbar-thin::-webkit-scrollbar-track {
                background-color: #f3f4f6;
                border-radius: 0.375rem;
              }

              .scrollbar-thin::-webkit-scrollbar-thumb:hover {
                background-color: #9ca3af;
              }
            `}</style>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {/* Appointment Types Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {appointmentTypes?.map((type) => (
                <div
                  key={type.value}
                  onClick={() => handleCardSelection("type", type.value)}
                  className={`group p-4 sm:p-6 border-2 rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 text-center relative overflow-hidden ${
                    formData.type === type.value
                      ? "border-blue-500 bg-gradient-to-b from-blue-50 to-purple-50 shadow-lg"
                      : "border-gray-200 hover:border-blue-300 bg-white hover:bg-gradient-to-b hover:from-blue-50 hover:to-purple-50"
                  }`}
                >
                  {/* Selection Indicator */}
                  {formData.type === type.value && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    </div>
                  )}

                  {/* Icon */}
                  <div className="text-4xl sm:text-5xl mb-3 group-hover:scale-110 transition-transform">
                    {type.icon}
                  </div>

                  {/* Label */}
                  <h4 className="font-semibold text-gray-800 text-sm sm:text-base leading-tight">
                    {type.label}
                  </h4>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {errors.type && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">
                  {errors.type}
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {/* Date & Time Selection */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendar Section */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-gray-700 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                  Select Date *
                </label>
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 sm:p-6 shadow-lg h-[500px] flex flex-col">
                  {/* Calendar Header */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      type="button"
                      onClick={() => navigateMonth(-1)}
                      className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <h4 className="text-lg sm:text-xl font-bold text-gray-800">
                      {monthNames[calendarDate.getMonth()]}{" "}
                      {calendarDate.getFullYear()}
                    </h4>
                    <button
                      type="button"
                      onClick={() => navigateMonth(1)}
                      className="p-3 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>

                  {/* Calendar Content - Flex Grow */}
                  <div className="flex-1 flex flex-col">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-3">
                      {dayNames?.map((day) => (
                        <div
                          key={day}
                          className="text-center text-xs sm:text-sm font-bold text-gray-500 py-2"
                        >
                          {day.substring(0, 3)}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1 flex-1">
                      {generateCalendar()?.map((dayData, index) => {
                        if (!dayData) {
                          return (
                            <div key={index} className="h-10 sm:h-12"></div>
                          );
                        }
                        const { day, date, isDisabled, isSelected, isToday } =
                          dayData;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleDateSelect(date)}
                            disabled={isDisabled}
                            className={`h-10 sm:h-12 text-sm sm:text-base rounded-xl font-semibold transition-all duration-200 ${
                              isSelected
                                ? "bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg transform"
                                : isToday
                                ? "bg-blue-100 text-blue-700 border-2 border-blue-300 font-bold"
                                : isDisabled
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-gray-700 hover:bg-gradient-to-br hover:from-blue-100 hover:to-purple-100 hover:text-blue-700"
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Date Error */}
                {errors.date && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-600 font-medium">
                      {errors.date}
                    </p>
                  </div>
                )}
              </div>

              {/* Time Selection Section */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-gray-700 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-purple-500" />
                  Select Time *
                </label>
                <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 sm:p-6 shadow-lg h-[500px] flex flex-col">
                  {/* Time Slots - Scrollable */}
                  <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    <div className="grid grid-cols-2 gap-2">
                      {timeSlots?.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleCardSelection("time", time)}
                          className={`p-3 sm:p-4 text-sm sm:text-base border-2 rounded-xl transition-all duration-200 font-semibold ${
                            formData.time === time
                              ? "border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 shadow-lg transform"
                              : "border-gray-200 hover:border-purple-300 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 hover:text-purple-600"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Time Error */}
                {errors.time && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-600 font-medium">
                      {errors.time}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Combined Selection Summary */}
            {formData.date && formData.time && (
              <div className="bg-gradient-to-r from-emerald-50 via-blue-50 to-purple-50 p-6 rounded-2xl border-2 border-emerald-200 shadow-lg">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center text-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-500 mr-2" />
                  Appointment Scheduled
                </h4>
                <p className="text-gray-700 font-semibold">
                  <span className="text-emerald-600">📅 Date & Time:</span>{" "}
                  {formatDate(formData.date)} at {formData.time}
                </p>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Compact Doctors Grid - Mobile Optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {doctors?.map((doctor) => (
                <div
                  key={doctor._id}
                  onClick={() => handleCardSelection("doctor", doctor._id)}
                  className={`group relative p-3 border-2 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-md ${
                    formData.doctor === doctor._id
                      ? "border-green-500 bg-gradient-to-r from-green-50 to-teal-50 shadow-md"
                      : "border-gray-200 hover:border-green-300 bg-white hover:bg-green-50"
                  }`}
                >
                  {/* Selection Indicator */}
                  {formData.doctor === doctor._id && (
                    <div className="absolute top-2 right-2 z-10">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                  )}

                  <div className="flex items-start space-x-3">
                    {/* Doctor Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-sm">
                        {doctor.avatar ? (
                          <img
                            src={doctor.avatar}
                            alt={doctor.name}
                            className="w-full h-full rounded-lg object-cover"
                          />
                        ) : (
                          doctor.name
                            .split(" ")
                            ?.map((n) => n[0])
                            .join("")
                        )}
                      </div>
                      {/* Online Status */}
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border border-white"></div>
                    </div>

                    {/* Doctor Info */}
                    <div className="flex-1 min-w-0">
                      {/* Name and Fee Row */}
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-bold text-gray-800 text-sm leading-tight truncate flex-1 mr-2">
                          Dr. {doctor.name}
                        </h4>
                        <div className="text-right flex-shrink-0 mr-5">
                          <p className="text-sm font-bold text-black">
                            ₹{doctor.consultationFee || 1500}
                          </p>
                          <p className="text-xs text-green-700">Fee</p>
                        </div>
                      </div>

                      {/* Specialization */}
                      <div className="mb-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md font-medium">
                          {doctor.specialization &&
                          doctor.specialization?.length > 0
                            ? doctor.specialization[0].length > 12
                              ? doctor.specialization[0].substring(0, 12) +
                                "..."
                              : doctor.specialization[0]
                            : "Internal Medicine"}
                        </span>
                        {doctor.specialization &&
                          doctor.specialization?.length > 1 && (
                            <span className="text-xs text-gray-500 ml-1">
                              +{doctor.specialization?.length - 1}
                            </span>
                          )}
                      </div>

                      {/* Education - Single Line */}
                      {doctor.education && doctor.education?.length > 0 && (
                        <p className="text-xs text-gray-600 mb-2 truncate">
                          🎓 {doctor.education[0].degree.split(" ")[0]} from{" "}
                          {doctor.education[0].institution.split(" ")[0]}
                        </p>
                      )}

                      {/* Contact */}
                      {/* <p className="text-xs text-gray-600 mb-2 truncate">
                        📞 {doctor.contact?.mobile || "+1234567890"}
                      </p> */}

                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center bg-amber-100 px-1.5 py-0.5 rounded">
                            <Star className="w-3 h-3 text-amber-500 fill-current mr-0.5" />
                            <span className="font-bold text-amber-700">
                              {doctor.rating || 4.5}
                            </span>
                          </div>
                          <span className="text-gray-600">
                            {doctor.experience || 10} yrs
                          </span>
                          {doctor.certifications &&
                            doctor.certifications?.length > 0 && (
                              <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                                {doctor.certifications?.length} Cert
                              </span>
                            )}
                        </div>
                        <div className="flex items-center text-green-600">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></div>
                          <span className="font-medium">Available</span>
                        </div>
                      </div>

                      {/* View More Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle view more logic here
                          console.log("View more details for:", doctor.name);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1 hover:underline"
                      >
                        View More
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {errors.doctor && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">
                  {errors.doctor}
                </p>
              </div>
            )}

            {/* Modal/Popup for detailed view - Add this component */}
            {/* You can implement a modal here that opens when "View More" is clicked */}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            {/* Appointment Summary */}
            <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 p-6 rounded-2xl border-2 border-indigo-200 shadow-lg">
              <h4 className="font-bold text-gray-800 mb-6 text-lg flex items-center">
                <CheckCircle className="w-6 h-6 text-indigo-600 mr-2" />
                Appointment Summary
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm sm:text-base">
                <div className="bg-white/80 p-4 rounded-xl border border-indigo-100">
                  <span className="font-bold text-indigo-700">🐾 Pet:</span>
                  <br />
                  <span className="text-gray-800">{selectedPet?.name}</span>
                </div>

                <div className="bg-white/80 p-4 rounded-xl border border-purple-100">
                  <span className="font-bold text-purple-700">🏥 Type:</span>
                  <br />
                  <span className="text-gray-800">
                    {
                      appointmentTypes?.find((t) => t.value === formData.type)
                        ?.label
                    }
                  </span>
                </div>

                <div className="bg-white/80 p-4 rounded-xl border border-pink-100">
                  <span className="font-bold text-pink-700">📅 Date:</span>
                  <br />
                  <span className="text-gray-800">
                    {formatDate(formData.date)}
                  </span>
                </div>

                <div className="bg-white/80 p-4 rounded-xl border border-indigo-100">
                  <span className="font-bold text-indigo-700">⏰ Time:</span>
                  <br />
                  <span className="text-gray-800">{formData.time}</span>
                </div>

                <div className="bg-white/80 p-4 rounded-xl border border-green-100">
                  <span className="font-bold text-green-700">👨‍⚕️ Doctor:</span>
                  <br />
                  <span className="text-gray-800">
                    Dr. {selectedDoctor?.name}
                  </span>
                </div>

                <div className="bg-white/80 p-4 rounded-xl border border-emerald-100">
                  <span className="font-bold text-emerald-700">💰 Fee:</span>
                  <br />
                  <span className="text-gray-800">
                    ₹{selectedDoctor?.consultationFee || 1500}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="space-y-4">
              <label className="block text-sm font-bold text-gray-700 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-indigo-500" />
                Additional Notes (Optional)
              </label>

              <div className="relative">
                <div className="absolute left-4 top-4 text-gray-400">
                  <FileText className="w-5 h-5" />
                </div>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder="Add any additional notes about the appointment, special requirements, or concerns..."
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none text-sm sm:text-base bg-white shadow-lg"
                />
              </div>

              <div className="flex items-center text-xs text-gray-500">
                <Info className="w-3 h-3 mr-1" />
                These notes will be shared with your veterinarian
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            {/* Payment Status - Success */}
            {isPaymentComplete && (
              <div className=" rounded-2xl p-6 flex items-center bg-emerald-50 border-2 border-emerald-200 shadow-lg">
                
                <div className="flex-1 text-center items-center">
                  <p className="text-emerald-800 font-bold text-lg mb-2">
                    🎉 Payment Completed Successfully!
                  </p>
                  <div className="space-y-1 text-sm">
                    <p className="text-emerald-700">
                      <span className="font-semibold">Payment ID:</span>{" "}
                      {paymentData?.paymentId}
                    </p>
                    <p className="text-emerald-700">
                      <span className="font-semibold">Amount:</span> ₹
                      {(selectedDoctor?.consultationFee || 1500) + 1}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Error */}
            {errors.payment && (
              <div className="flex items-start">
                <AlertCircle className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-red-800 font-bold text-lg mb-2">
                    Payment Error
                  </p>
                  <p className="text-red-600 text-sm">{errors.payment}</p>
                </div>
              </div>
            )}

            {/* Payment Component Container */}
            {!isPaymentComplete && (
              <div className="">
                <PaymentComponent
                  amount={(selectedDoctor?.consultationFee || 1500) + 1}
                  appointmentDetails={{
                    type: formData.type,
                    date: formData.date,
                    time: formData.time,
                    notes: formData.notes,
                    appointmentId: formData.appointmentId || "",
                  }}
                  formData={formData}
                  petDetails={selectedPet || {}}
                  doctorDetails={selectedDoctor || {}}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentFailure={handlePaymentFailure}
                  disabled={isPaymentComplete}
                />
              </div>
            )}

            {/* Processing Indicator */}
            {isPaymentComplete && submitting && (
              <div className="text-center py-12">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <div className="space-y-2">
                  <p className="text-lg font-semibold text-gray-800">
                    Creating your appointment...
                  </p>
                  <p className="text-sm text-gray-600">
                    Please wait while we process your booking
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 7:
        // Calculate total fee
        totalFee = (selectedDoctor?.consultationFee || 1500) + 1;

        return (
          <div className="space-y-6">
            {/* Appointment Confirmation Card */}
            <div className="bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 border-2 border-emerald-200 rounded-3xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-blue-500/5"></div>

              <div className="relative">
                {/* Header */}
                <div className="text-center border-b-2 border-emerald-200 pb-6 mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-xl">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-emerald-800 mb-2">
                    🐾 Animalia+ Veterinary
                  </h2>
                  <p className="text-emerald-700 font-semibold text-lg">
                    Appointment Confirmation
                  </p>
                  <div className="mt-4 inline-flex items-center bg-blue-100 px-4 py-2 rounded-full">
                    <span className="text-sm font-bold text-blue-800">
                      ID:{" "}
                      <span className="font-mono">{getAppointmentId()}</span>
                    </span>
                  </div>
                </div>

                {/* Appointment Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Pet & Owner Info */}
                  <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border border-emerald-100 shadow-lg">
                    <h4 className="font-bold text-emerald-800 mb-4 text-lg flex items-center">
                      <Heart className="w-5 h-5 mr-2" />
                      Pet Information
                    </h4>
                    <div className="space-y-3 text-sm sm:text-base">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Pet Name:
                        </span>
                        <span className="text-gray-900">
                          {selectedPet?.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Species:
                        </span>
                        <span className="text-gray-900">
                          {selectedPet?.species}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Breed:
                        </span>
                        <span className="text-gray-900">
                          {selectedPet?.breed}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Owner:
                        </span>
                        <span className="text-gray-900">
                          {selectedPet?.ownerId?.userDetails?.name}{" "}
                          {selectedPet?.ownerId?.userDetails?.lastName}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Doctor Info */}
                  <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border border-blue-100 shadow-lg">
                    <h4 className="font-bold text-blue-800 mb-4 text-lg flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Doctor Information
                    </h4>
                    <div className="space-y-3 text-sm sm:text-base">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Doctor:
                        </span>
                        <span className="text-gray-900">
                          Dr. {selectedDoctor?.name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Specialization:
                        </span>
                        <span className="text-gray-900">
                          {selectedDoctor?.specialization?.[0] ||
                            "General Medicine"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Experience:
                        </span>
                        <span className="text-gray-900">
                          {selectedDoctor?.experience || 5} years
                        </span>
                      </div>
                      {/* <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Contact:
                        </span>
                        <span className="text-gray-900">
                          {selectedDoctor?.contact?.mobile || "Not provided"}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </div>

                {/* Appointment Schedule */}
                <div className=" backdrop-blur-sm py-6 rounded-2xl border  mb-8">
                  <h4 className="font-bold text-gray-800 mb-6 text-lg flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Appointment Schedule
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="text-center bg-gradient-to-br from-blue-50 to-blue-100 py-4 rounded-xl border border-blue-200">
                      <div className="text-2xl mb-2">🏥</div>
                      <p className="font-bold text-blue-800 text-sm">
                        Appointment Type
                      </p>
                      <p className="text-blue-700 font-semibold">
                        {
                          appointmentTypes?.find(
                            (t) => t.value === formData.type
                          )?.label
                        }
                      </p>
                    </div>

                    <div className="text-center bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                      <div className="text-2xl mb-2">📅</div>
                      <p className="font-bold text-purple-800 text-sm">Date</p>
                      <p className="text-purple-700 font-semibold">
                        {formatDate(formData.date)}
                      </p>
                    </div>

                    <div className="text-center bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                      <div className="text-2xl mb-2">⏰</div>
                      <p className="font-bold text-orange-800 text-sm">Time</p>
                      <p className="text-orange-700 font-semibold">
                        {formData.time}
                      </p>
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-emerald-100 to-green-100 border-2 border-emerald-200">
                      <CheckCircle className="w-5 h-5 mr-2 text-emerald-600" />
                      <span className="font-bold text-emerald-800">
                        Confirmed & Paid
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 shadow-lg mb-8">
                  <h4 className="font-bold text-gray-800 mb-6 text-lg flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payment Details
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Payment ID:
                        </span>
                        <span className="text-gray-900 font-mono text-xs">
                          {paymentData?.paymentId}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Order ID:
                        </span>
                        <span className="text-gray-900 font-mono text-xs">
                          {paymentData?.orderId}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Payment Method:
                        </span>
                        <span className="text-gray-900">Razorpay</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-700">
                          Status:
                        </span>
                        <span className="text-emerald-600 font-bold flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Completed
                        </span>
                      </div>
                    </div>

                    {/* Bill Breakdown */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">
                            Consultation Fee:
                          </span>
                          <span className="font-semibold">
                            ₹{selectedDoctor?.consultationFee || 1500}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Service Charge:</span>
                          <span className="font-semibold">₹1</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg pt-3 border-t-2 border-gray-300">
                          <span className="text-gray-800">Total Paid:</span>
                          <span className="text-emerald-600">₹{totalFee}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                {formData.notes && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl p-6 mb-8">
                    <h4 className="font-bold text-amber-800 mb-3 flex items-center">
                      <FileText className="w-5 h-5 mr-2" />
                      Additional Notes
                    </h4>
                    <p className="text-amber-700 leading-relaxed">
                      {formData.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Important Instructions */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
              <h4 className="font-bold text-blue-800 mb-4 text-lg flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                Important Instructions
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      Please arrive 15 minutes before your appointment time
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      Bring your pet's vaccination records and medical history
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Keep your pet calm and on a leash/in carrier</span>
                  </li>
                </ul>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      Contact us at least 24 hours in advance for rescheduling
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>Bring this confirmation for reference</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-2">•</span>
                    <span>
                      Our team will call you 1 hour before the appointment
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <button
                onClick={handlePrint}
                className="flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
                Print Confirmation
              </button>
            </div>

            {/* Footer Message */}
            <div className="text-center text-gray-600 border-t-2 border-gray-200 pt-8 mt-8">
              <div className="space-y-2">
                <p className="text-lg font-semibold text-emerald-600">
                  Thank you for choosing Animalia+ Veterinary Services! 🏥
                </p>
                <p className="text-sm">
                  We look forward to caring for your beloved pet. 🐾❤️
                </p>
                {/* <div className="flex justify-center space-x-4 mt-4 text-xs text-gray-500">
                  <span>📞 Emergency: (555) 911-PETS</span>
                  <span>📧 support@animalia.vet</span>
                </div> */}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get appointment ID
  const getAppointmentId = () => {
    return paymentData?.orderId || "APT-" + Date.now().toString().slice(-6);
  };

  return (
  <div className={`min-h-screen flex flex-col ${className}`}>
    {/* Fixed Header */}
    <div className="sticky top-4 z-10  border-b bg-white border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">Success!</p>
              <p className="text-green-700 text-sm mt-1">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-2">
            {currentStep === 7
              ? "Appointment Confirmed"
              : "Create Appointment"}
          </h1>

          <p className="text-gray-600 max-w-2xl text-sm sm:text-base">
            {currentStep === 7
              ? "Your booking is complete and confirmed"
              : "Schedule a visit for your pet with our veterinary team"}
          </p>
        </div>

        {/* Progress Steps */}
        <div>
          {/* Mobile Progress */}
          <div className="block md:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((currentStep / totalSteps) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {steps?.find((step) => step.number === currentStep)?.title}
            </p>
          </div>

          {/* Desktop Progress */}
          <div className="hidden md:block">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === step.number;
                const isCompleted =
                  completedSteps.has(step.number) || currentStep > step.number;
                const isClickable =
                  step.number <= currentStep ||
                  completedSteps.has(step.number - 1);

                return (
                  <div key={step.number} className="flex items-center flex-1">
                    <div className="relative flex flex-col items-center">
                      <div
                        onClick={() =>
                          isClickable && handleStepClick(step.number)
                        }
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-200 ${
                          isCompleted
                            ? "bg-green-600 border-green-600 text-white"
                            : isActive
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-gray-300 text-gray-400"
                        } ${
                          isClickable
                            ? "cursor-pointer hover:border-gray-400"
                            : "cursor-default"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>

                      <span
                        className={`text-xs mt-2 font-medium text-center max-w-20 ${
                          isActive
                            ? "text-blue-600"
                            : isCompleted
                            ? "text-green-600"
                            : "text-gray-500"
                        }`}
                      >
                        {step.title}
                      </span>
                    </div>

                    {index < steps?.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-4 ${
                          isCompleted || currentStep > step.number
                            ? "bg-green-600"
                            : "bg-gray-300"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Scrollable Content */}
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto  py-6">
        {/* Error Message */}
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <span className="text-red-800 font-medium">{errors.submit}</span>
          </div>
        )}

        {renderStepContent()}
      </div>
    </div>

    {/* Fixed Footer */}
    <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center gap-4">
          {/* Previous Button */}
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1 || currentStep === 7}
            className="flex items-center justify-center px-4 sm:px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </button>

          {/* Progress Dots - Desktop Only */}
          <div className="hidden sm:flex items-center space-x-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  i < currentStep ? "bg-blue-600" : "bg-gray-300"
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-3">
              {currentStep} of {totalSteps}
            </span>
          </div>

          {/* Next/Action Button */}
          {currentStep < 6 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center justify-center px-4 sm:px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 font-medium"
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
            </button>
          ) : currentStep === 6 ? (
            <button
              type="button"
              onClick={() => {
                if (isPaymentComplete) {
                  setCurrentStep(7);
                }
              }}
              disabled={!isPaymentComplete}
              className={`px-4 sm:px-6 py-2.5 rounded-lg focus:outline-none focus:ring-2 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-200 font-medium ${
                isPaymentComplete
                  ? "bg-green-600 hover:bg-green-700 text-white focus:ring-green-500"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }`}
            >
              {isPaymentComplete ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">View Confirmation</span>
                  <span className="sm:hidden">Confirm</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Complete Payment</span>
                  <span className="sm:hidden">Pay</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center justify-center px-4 sm:px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200 font-medium"
            >
              <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">New Appointment</span>
              <span className="sm:hidden">New</span>
            </button>
          )}
        </div>

        {/* Mobile Progress - Footer */}
        <div className="block sm:hidden mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                  i < currentStep ? "bg-blue-600" : "bg-gray-300"
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-2">
              {currentStep} of {totalSteps}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

export default AppointmentForm;
